// LES ENSEMBLES D'OBJETS DE GLASS, sans GPU : six ensembles de 2 à 5 objets de
// verre (type, taille, orbite), leur placement dans le temps autour du centre
// de composition (R19), le cadrage « couvrir » du fond avec sa dérive Ken Burns
// (R18 : la marge de dérive est comprise dans l'échelle), le pli kaléidoscope.
//
// Unités d'écran : hauteur 2 (y dans [-1, 1]), x dans [-A, A] (A : l'aspect).
// Les objets vivent dans un plan devant le fond ; la caméra virtuelle est à
// CAM_D du plan z = 0, champ tel que ce plan fasse exactement l'écran.

export const TYPES = ['prism', 'sphere', 'cube', 'crystal', 'lens'];
export const SETS = [
  [{ t: 'prism', s: 0.42, r: 0.62, w: 0.21, ph: 0.0 }, { t: 'sphere', s: 0.34, r: 0.70, w: 0.15, ph: 2.3 }, { t: 'lens', s: 0.40, r: 0.78, w: 0.11, ph: 4.4 }],
  [{ t: 'crystal', s: 0.46, r: 0.55, w: 0.17, ph: 1.0 }, { t: 'cube', s: 0.36, r: 0.80, w: 0.13, ph: 3.6 }],
  [{ t: 'prism', s: 0.38, r: 0.74, w: 0.19, ph: 0.4 }, { t: 'prism', s: 0.32, r: 0.74, w: 0.19, ph: 3.5 }, { t: 'sphere', s: 0.30, r: 0.50, w: 0.23, ph: 1.9 }, { t: 'cube', s: 0.28, r: 0.86, w: 0.12, ph: 5.2 }],
  [{ t: 'lens', s: 0.52, r: 0.40, w: 0.09, ph: 0.0 }, { t: 'crystal', s: 0.34, r: 0.82, w: 0.16, ph: 2.1 }, { t: 'sphere', s: 0.26, r: 0.84, w: 0.20, ph: 4.2 }],
  [{ t: 'cube', s: 0.40, r: 0.66, w: 0.14, ph: 0.8 }, { t: 'prism', s: 0.40, r: 0.66, w: 0.14, ph: 2.9 }, { t: 'crystal', s: 0.40, r: 0.66, w: 0.14, ph: 5.0 }],
  [{ t: 'sphere', s: 0.44, r: 0.58, w: 0.12, ph: 0.0 }, { t: 'lens', s: 0.36, r: 0.78, w: 0.18, ph: 1.3 }, { t: 'prism', s: 0.30, r: 0.86, w: 0.15, ph: 2.6 }, { t: 'cube', s: 0.28, r: 0.86, w: 0.15, ph: 3.9 }, { t: 'crystal', s: 0.30, r: 0.70, w: 0.10, ph: 5.3 }]
];
export const CAM_D = 3.2;
export const BG_Z = -1.4;                                 // le plan du fond, derrière les objets (z dans [-0,75, 0,75])

// la position d'un objet au temps t (s) : une orbite elliptique autour du centre,
// un flottement en z, resserrée par la basse (low) ; l'écran a l'aspect A
export function placeObject(o, i, t, A, low, align) {
  const a = o.ph + t * o.w;
  const pull = 1 - 0.22 * (low || 0);
  // l'orbite est bornée pour que l'objet entier (les maillages ont un rayon de 1 :
  // rayon projeté = o.s x 1,12 basse x 1,12 respiration, grossi 1,08 par la
  // perspective à z = +0,22) reste dans le cadre avec la marge de 5 % (R6) et
  // sous la zone du nom de station
  const PERSP = 1.08, half = o.s * 1.26;
  const rx = Math.min(o.r * Math.min(A * 0.85, 1.35), (A - 0.12) / PERSP - half) * pull;
  const ry = Math.min(o.r * 0.78, 0.85 / PERSP - half) * pull;
  let x = Math.cos(a) * rx, y = Math.sin(a * 0.9 + 0.7) * ry, z = 0.22 * Math.sin(t * 0.37 + i * 1.3);
  // le drop : alignés devant le centre, en rang serré
  if (align > 0) {
    const n = 5, ax = (i - 1.5) * 0.55 * Math.min(A, 1.6) / 1.6;
    x = x * (1 - align) + ax * align; y = y * (1 - align); z = z * (1 - align) + 0.35 * align;
  }
  const scale = o.s * (1 + 0.12 * (low || 0));
  return { x, y, z, scale, angle: a };
}

// le cadrage du fond (R18) : échelle = max des deux rapports, marge de dérive
// comprise ; renvoie l'échelle UV (fraction de l'image visible par axe) et le
// décalage Ken Burns (dans la marge)
export const DRIFT = 0.06;                                  // la part de l'image gardée pour la dérive
export function coverFrame(A, coverAspect, t) {
  const screenAspect = A;                                   // largeur / hauteur
  // l'image couvre : si elle est plus large que l'écran, toute sa hauteur est visible, sinon toute sa largeur
  let visW, visH;
  if (coverAspect > screenAspect) { visH = 1; visW = screenAspect / coverAspect; } else { visW = 1; visH = coverAspect / screenAspect; }
  const zoom = 1 + DRIFT;                                   // zoom légèrement : la marge de dérive
  visW /= zoom; visH /= zoom;
  const mx = (1 - visW) / 2, my = (1 - visH) / 2;          // les marges disponibles
  const dx = Math.sin(t * 0.07) * Math.min(mx, DRIFT / 2), dy = Math.cos(t * 0.053) * Math.min(my, DRIFT / 2);
  return { visW, visH, cx: 0.5 + dx, cy: 0.5 + dy };
}
// la dérive reste dans l'image : les bords de la fenêtre UV restent dans [0, 1]
export function frameInside(f) { return f.cx - f.visW / 2 >= -1e-9 && f.cx + f.visW / 2 <= 1 + 1e-9 && f.cy - f.visH / 2 >= -1e-9 && f.cy + f.visH / 2 <= 1 + 1e-9; }

// le pli kaléidoscope (6 secteurs) d'un point d'écran autour du centre, comme le shader
export function kaleido(x, y, sectors) {
  const n = sectors || 6;
  let a = Math.atan2(y, x), r = Math.hypot(x, y);
  const w = (2 * Math.PI) / n;
  a = ((a % w) + w) % w;
  if (a > w / 2) a = w - a;
  return [Math.cos(a) * r, Math.sin(a) * r];
}
